import type { ComponentAggregate, OptionCount } from '@/lib/api/xurvey';

/**
 * Cómo se pinta cada pregunta de elección del informe.
 *
 * El informe no sabe de antemano qué preguntas trae una encuesta: cada marca
 * pide lo suyo por paso. Lo que sí sabe es leer la pregunta —su clave, su
 * etiqueta, sus opciones— y elegir la gráfica que el diseño reservó para esa
 * familia: la edad es una escalera, el país una rosquilla, el género una
 * balanza, el origen un waffle de 100 personas, un sí/no un medidor, y lo
 * demás una lista con barras. Es una heurística y se dice: si la pregunta no
 * casa con ninguna familia, cae en la lista, nunca se pierde.
 */
export type Familia = 'edad' | 'genero' | 'pais' | 'origen' | 'sino' | 'lista';

export interface Dimension {
  familia: Familia;
  componente: ComponentAggregate;
  opciones: OptionCount[];
  /** El nombre de la encuesta, sólo cuando el alcance mezcla varias. */
  encuesta?: string;
}

/** Componentes que responden algo pero no son una pregunta que se grafique. */
const SIN_GRAFICA = new Set([
  'consentForm',
  'game',
  'ageGate',
  'languageSelector',
  'welcomeScreen',
  'completionScreen',
  'passport',
  'thanks',
]);

const RE_EDAD = /\b(age|edad|años|anos|rango de edad|age group|age range)\b/i;
const RE_GENERO = /\b(gender|g[eé]nero|sexo|identif)/i;
const RE_PAIS = /\b(country|pa[ií]s|origin|origen|nationality|nacionalidad|born|naci[oó])/i;
const RE_ORIGEN = /\b(background|ethnic|etnia|raza|race|hispanic|latino|heritage|descend|ancestr)/i;
const RE_OPCION_GENERO = /^(woman|women|female|mujer|man|men|male|hombre|non[- ]?binary|no binario|no binaria|prefer not|prefiero no)/i;
const RE_SI = /^(yes|s[ií]|true)$/i;
const RE_NO = /^(no|false)$/i;

export function familiaDe(c: ComponentAggregate): Familia | null {
  if (c.kind !== 'choice' || SIN_GRAFICA.has(c.type)) return null;
  const opciones = c.options ?? [];
  if (opciones.length === 0) return null;
  const texto = `${c.key ?? ''} ${c.label ?? ''}`;
  // Sí/no: dos respuestas y las dos son sí o no. Antes que nada, porque
  // «¿Has enviado dinero al país de origen?» también casaría con «país».
  if (opciones.length <= 3) {
    const sinos = opciones.filter((o) => RE_SI.test(o.label) || RE_NO.test(o.label) || RE_SI.test(o.value) || RE_NO.test(o.value));
    if (sinos.length >= 2 && opciones.some((o) => RE_SI.test(o.label) || RE_SI.test(o.value))) return 'sino';
  }
  if (RE_EDAD.test(texto) || opciones.filter((o) => /^\d{2}\s*[-–]\s*\d{2}|^\d{2}\s*\+/.test(o.label)).length >= 2) return 'edad';
  if (RE_GENERO.test(texto) || opciones.filter((o) => RE_OPCION_GENERO.test(o.label)).length >= 2) return 'genero';
  if (RE_ORIGEN.test(texto)) return 'origen';
  if (RE_PAIS.test(texto)) return 'pais';
  return 'lista';
}

/** La edad se lee en orden de franja, no por frecuencia. */
function ordenarEdad(opciones: OptionCount[]): OptionCount[] {
  const numero = (o: OptionCount) => {
    const m = o.label.match(/\d+/);
    return m ? Number(m[0]) : Number.MAX_SAFE_INTEGER;
  };
  return [...opciones].sort((a, b) => numero(a) - numero(b));
}

export function dimensionesDe(
  agregados: Array<{ nombre: string; components: ComponentAggregate[] }>,
): Dimension[] {
  const varias = agregados.length > 1;
  const out: Dimension[] = [];
  for (const a of agregados) {
    for (const c of a.components) {
      const familia = familiaDe(c);
      if (!familia || c.answered === 0) continue;
      const opciones = familia === 'edad' ? ordenarEdad(c.options ?? []) : (c.options ?? []);
      out.push({ familia, componente: c, opciones, ...(varias ? { encuesta: a.nombre } : {}) });
    }
  }
  // El orden del diseño: origen y persona arriba, luego edad, país, género, sí/no, y el resto.
  const peso: Record<Familia, number> = { origen: 0, edad: 1, pais: 2, genero: 3, sino: 4, lista: 5 };
  return out.sort((x, y) => peso[x.familia] - peso[y.familia]);
}

/** La respuesta más frecuente de una dimensión, para «el visitante compuesto». */
export function dominante(d: Dimension): OptionCount | undefined {
  return [...d.opciones].sort((a, b) => b.count - a.count)[0];
}

/** Una lectura de una línea: la franja mayor y qué parte del total es. */
export function titularDe(d: Dimension): { label: string; pct: number } | null {
  const top = dominante(d);
  if (!top) return null;
  return { label: top.label, pct: Math.round(top.pct) };
}

/** Celdas de un waffle de 100: cada opción, redondeada, y el resto en gris. */
export function celdasWaffle(opciones: OptionCount[]): number[] {
  const total = opciones.reduce((s, o) => s + o.count, 0) || 1;
  const celdas = opciones.map((o) => Math.round((o.count / total) * 100));
  // Que sumen exactamente 100: la más grande absorbe el redondeo.
  const suma = celdas.reduce((s, n) => s + n, 0);
  if (celdas.length) celdas[celdas.indexOf(Math.max(...celdas))]! += 100 - suma;
  return celdas;
}
